import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  X,
  Save,
  Loader,
  Layout,
  MousePointer,
  Zap,
  Hash,
  Filter,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import featuresService from '../services/featuresService';
import { useToast } from '../context/ToastContext';
import './FeaturesPage.css';

const FeaturesPage = () => {
  const { i18n } = useTranslation();
  const toast = useToast();
  const isRTL = i18n.language === 'ar';

  const [features, setFeatures] = useState([]);
  const [groupedFeatures, setGroupedFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    nameAr: '',
    nameEn: '',
    descriptionAr: '',
    descriptionEn: '',
    type: 'page',
    category: 'other',
    route: '',
    componentId: '',
    defaultValue: '',
    icon: 'Settings',
    order: 0
  });

  const featureTypes = [
    { value: 'page', label: isRTL ? 'صفحة' : 'Page', icon: Layout },
    { value: 'button', label: isRTL ? 'زر' : 'Button', icon: MousePointer },
    { value: 'action', label: isRTL ? 'إجراء' : 'Action', icon: Zap },
    { value: 'limit', label: isRTL ? 'حد' : 'Limit', icon: Hash }
  ];

  const categories = [
    { value: 'offers', label: isRTL ? 'العروض' : 'Offers' },
    { value: 'requests', label: isRTL ? 'الطلبات' : 'Requests' },
    { value: 'chat', label: isRTL ? 'المحادثات' : 'Chat' },
    { value: 'network', label: isRTL ? 'الشبكة' : 'Network' },
    { value: 'catalog', label: isRTL ? 'الكتالوج' : 'Catalog' },
    { value: 'statistics', label: isRTL ? 'الإحصائيات' : 'Statistics' },
    { value: 'settings', label: isRTL ? 'الإعدادات' : 'Settings' },
    { value: 'other', label: isRTL ? 'أخرى' : 'Other' }
  ];

  const iconOptions = [
    'Settings', 'Layout', 'Building2', 'ClipboardList', 'Globe', 'MessageCircle',
    'BookOpen', 'BarChart3', 'Receipt', 'Users', 'FileCheck', 'Plus', 'Edit',
    'Trash2', 'Share2', 'Upload', 'Download', 'Sparkles', 'UserPlus', 'Send',
    'Hash', 'Image', 'Star', 'Crown', 'Zap', 'Eye', 'Lock', 'Unlock'
  ];

  // جلب الميزات
  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.type = filterType;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (searchQuery) params.search = searchQuery;

      const response = await featuresService.getAll(params);
      if (response.success) {
        setFeatures(response.data.features || []);
        setGroupedFeatures(response.data.groupedFeatures || {});
      }
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error(isRTL ? 'فشل في جلب الميزات' : 'Failed to fetch features');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterCategory, searchQuery, toast, isRTL]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // فتح modal للإنشاء
  const handleCreate = () => {
    setFormData({
      code: '',
      nameAr: '',
      nameEn: '',
      descriptionAr: '',
      descriptionEn: '',
      type: 'page',
      category: 'other',
      route: '',
      componentId: '',
      defaultValue: '',
      icon: 'Settings',
      order: 0
    });
    setModalMode('create');
    setShowModal(true);
  };

  // فتح modal للتعديل
  const handleEdit = (feature) => {
    setFormData({
      _id: feature._id,
      code: feature.code,
      nameAr: feature.nameAr,
      nameEn: feature.nameEn,
      descriptionAr: feature.descriptionAr || '',
      descriptionEn: feature.descriptionEn || '',
      type: feature.type,
      category: feature.category,
      route: feature.route || '',
      componentId: feature.componentId || '',
      defaultValue: feature.defaultValue || '',
      icon: feature.icon || 'Settings',
      order: feature.order || 0
    });
    setModalMode('edit');
    setShowModal(true);
  };

  // حفظ الميزة
  const handleSave = async () => {
    if (!formData.code || !formData.nameAr || !formData.nameEn || !formData.type) {
      toast.warning(isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      if (modalMode === 'create') {
        const response = await featuresService.create(formData);
        if (response.success) {
          toast.success(isRTL ? 'تم إنشاء الميزة بنجاح' : 'Feature created successfully');
          setShowModal(false);
          fetchFeatures();
        } else {
          toast.error(response.message || (isRTL ? 'فشل الإنشاء' : 'Failed to create'));
        }
      } else {
        const response = await featuresService.update(formData._id, formData);
        if (response.success) {
          toast.success(isRTL ? 'تم تحديث الميزة بنجاح' : 'Feature updated successfully');
          setShowModal(false);
          fetchFeatures();
        } else {
          toast.error(response.message || (isRTL ? 'فشل التحديث' : 'Failed to update'));
        }
      }
    } catch (error) {
      console.error('Error saving feature:', error);
      toast.error(error.message || (isRTL ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setSaving(false);
    }
  };

  // تفعيل/تعطيل ميزة
  const handleToggle = async (feature) => {
    try {
      const response = await featuresService.toggle(feature._id);
      if (response.success) {
        toast.success(
          feature.isActive
            ? (isRTL ? 'تم تعطيل الميزة' : 'Feature deactivated')
            : (isRTL ? 'تم تفعيل الميزة' : 'Feature activated')
        );
        fetchFeatures();
      }
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error(error.message);
    }
  };

  // حذف ميزة
  const handleDelete = async (feature) => {
    if (!window.confirm(isRTL ? 'هل أنت متأكد من حذف هذه الميزة؟' : 'Are you sure you want to delete this feature?')) {
      return;
    }

    try {
      const response = await featuresService.delete(feature._id);
      if (response.success) {
        toast.success(isRTL ? 'تم حذف الميزة بنجاح' : 'Feature deleted successfully');
        fetchFeatures();
      }
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error(error.message);
    }
  };

  // إنشاء ميزات افتراضية
  const handleSeedDefaults = async () => {
    if (!window.confirm(isRTL ? 'هل تريد إنشاء الميزات الافتراضية؟' : 'Do you want to create default features?')) {
      return;
    }

    try {
      const response = await featuresService.seedDefaults();
      if (response.success) {
        toast.success(response.message || (isRTL ? 'تم إنشاء الميزات الافتراضية' : 'Default features created'));
        fetchFeatures();
      }
    } catch (error) {
      console.error('Error seeding features:', error);
      toast.error(error.message);
    }
  };

  const getTypeIcon = (type) => {
    const typeObj = featureTypes.find(t => t.value === type);
    return typeObj ? typeObj.icon : Settings;
  };

  const getTypeLabel = (type) => {
    const typeObj = featureTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getCategoryLabel = (category) => {
    const catObj = categories.find(c => c.value === category);
    return catObj ? catObj.label : category;
  };

  return (
    <div className="features-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-title">
          <Settings size={28} />
          <h1>{isRTL ? 'إدارة الميزات والصلاحيات' : 'Features & Permissions'}</h1>
          <span className="count">{features.length}</span>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={handleSeedDefaults}>
            <Sparkles size={18} />
            {isRTL ? 'إنشاء افتراضية' : 'Seed Defaults'}
          </button>
          <button className="btn btn-primary" onClick={handleCreate}>
            <Plus size={18} />
            {isRTL ? 'إضافة ميزة' : 'Add Feature'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder={isRTL ? 'بحث...' : 'Search...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">{isRTL ? 'جميع الأنواع' : 'All Types'}</option>
            {featureTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="all">{isRTL ? 'جميع التصنيفات' : 'All Categories'}</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <button className="btn btn-icon" onClick={fetchFeatures} title={isRTL ? 'تحديث' : 'Refresh'}>
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <Loader className="spin" size={40} />
          <p>{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      ) : features.length === 0 ? (
        <div className="empty-state">
          <Settings size={64} />
          <h3>{isRTL ? 'لا توجد ميزات' : 'No Features'}</h3>
          <p>{isRTL ? 'ابدأ بإنشاء الميزات الافتراضية أو أضف ميزة جديدة' : 'Start by seeding defaults or add a new feature'}</p>
          <button className="btn btn-primary" onClick={handleSeedDefaults}>
            <Sparkles size={18} />
            {isRTL ? 'إنشاء ميزات افتراضية' : 'Seed Default Features'}
          </button>
        </div>
      ) : (
        <div className="features-grid">
          {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
            <div key={category} className="category-section">
              <h2 className="category-title">{getCategoryLabel(category)}</h2>
              <div className="features-list">
                {categoryFeatures.map(feature => {
                  const TypeIcon = getTypeIcon(feature.type);
                  return (
                    <div key={feature._id} className={`feature-card ${!feature.isActive ? 'inactive' : ''}`}>
                      <div className="feature-header">
                        <div className="feature-icon">
                          <TypeIcon size={20} />
                        </div>
                        <div className="feature-info">
                          <h3>{isRTL ? feature.nameAr : feature.nameEn}</h3>
                          <span className="feature-code">{feature.code}</span>
                        </div>
                        <span className={`type-badge ${feature.type}`}>
                          {getTypeLabel(feature.type)}
                        </span>
                      </div>

                      {(feature.route || feature.componentId || feature.defaultValue) && (
                        <div className="feature-details">
                          {feature.route && (
                            <span className="detail-item">
                              <Layout size={14} /> {feature.route}
                            </span>
                          )}
                          {feature.componentId && (
                            <span className="detail-item">
                              <MousePointer size={14} /> {feature.componentId}
                            </span>
                          )}
                          {feature.defaultValue !== null && feature.defaultValue !== undefined && (
                            <span className="detail-item">
                              <Hash size={14} /> {String(feature.defaultValue)}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="feature-actions">
                        <button
                          className={`action-btn ${feature.isActive ? 'active' : 'inactive'}`}
                          onClick={() => handleToggle(feature)}
                          title={feature.isActive ? (isRTL ? 'تعطيل' : 'Deactivate') : (isRTL ? 'تفعيل' : 'Activate')}
                        >
                          {feature.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(feature)}
                          title={isRTL ? 'تعديل' : 'Edit'}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDelete(feature)}
                          title={isRTL ? 'حذف' : 'Delete'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Settings size={22} />
                {modalMode === 'create'
                  ? (isRTL ? 'إضافة ميزة جديدة' : 'Add New Feature')
                  : (isRTL ? 'تعديل الميزة' : 'Edit Feature')
                }
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{isRTL ? 'الكود (فريد)' : 'Code (Unique)'} *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="PAGE_EXAMPLE"
                    disabled={modalMode === 'edit'}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="form-group">
                  <label>{isRTL ? 'النوع' : 'Type'} *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    {featureTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{isRTL ? 'الاسم بالعربي' : 'Name (Arabic)'} *</label>
                  <input
                    type="text"
                    value={formData.nameAr}
                    onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                    placeholder={isRTL ? 'اسم الميزة' : 'Feature name in Arabic'}
                  />
                </div>
                <div className="form-group">
                  <label>{isRTL ? 'الاسم بالإنجليزي' : 'Name (English)'} *</label>
                  <input
                    type="text"
                    value={formData.nameEn}
                    onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                    placeholder="Feature name in English"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{isRTL ? 'التصنيف' : 'Category'}</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{isRTL ? 'الأيقونة' : 'Icon'}</label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.type === 'page' && (
                <div className="form-group">
                  <label>{isRTL ? 'مسار الصفحة' : 'Page Route'}</label>
                  <input
                    type="text"
                    value={formData.route}
                    onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                    placeholder="/dashboard"
                  />
                </div>
              )}

              {(formData.type === 'button' || formData.type === 'action') && (
                <div className="form-group">
                  <label>{isRTL ? 'معرف المكون' : 'Component ID'}</label>
                  <input
                    type="text"
                    value={formData.componentId}
                    onChange={(e) => setFormData({ ...formData, componentId: e.target.value })}
                    placeholder="add-offer-btn"
                  />
                </div>
              )}

              {formData.type === 'limit' && (
                <div className="form-group">
                  <label>{isRTL ? 'القيمة الافتراضية' : 'Default Value'}</label>
                  <input
                    type="number"
                    value={formData.defaultValue}
                    onChange={(e) => setFormData({ ...formData, defaultValue: parseInt(e.target.value) || 0 })}
                    placeholder="5"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>{isRTL ? 'الوصف بالعربي' : 'Description (Arabic)'}</label>
                  <textarea
                    value={formData.descriptionAr}
                    onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="form-group">
                  <label>{isRTL ? 'الوصف بالإنجليزي' : 'Description (English)'}</label>
                  <textarea
                    value={formData.descriptionEn}
                    onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{isRTL ? 'الترتيب' : 'Order'}</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <Loader className="spin" size={18} /> : <Save size={18} />}
                {modalMode === 'create'
                  ? (isRTL ? 'إنشاء' : 'Create')
                  : (isRTL ? 'حفظ التغييرات' : 'Save Changes')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturesPage;
